/* ══════════════════════════════════════════════════════════════
   Runtime Configuration Service — Environment-based config
   Reads VITE_* env vars and provides typed runtime config
   ══════════════════════════════════════════════════════════════ */

import { isTauri } from './runtime';
import { getFeatureFlags, type FeatureFlags, type FeatureFlagKey } from '../config/feature-flags';

export interface RuntimeConfig {
    apiBaseUrl: string;
    variant: 'india' | 'india-economy' | 'india-tech';
    isDev: boolean;
    isProd: boolean;
    enableAnalytics: boolean;
    enableServiceWorker: boolean;
    enableNotifications: boolean;
    groqApiEnabled: boolean;
    upstoxEnabled: boolean;
    debugMode: boolean;
    version: string;
    featureFlags: FeatureFlags;
}

let config: RuntimeConfig | null = null;

/** Build runtime config from environment */
function buildConfig(): RuntimeConfig {
    const isDev = import.meta.env.DEV;
    const featureFlags = getFeatureFlags();

    return {
        apiBaseUrl: isDev ? '' : (import.meta.env.VITE_API_BASE_URL || ''),
        variant: (import.meta.env.VITE_VARIANT || 'india') as RuntimeConfig['variant'],
        isDev,
        isProd: import.meta.env.PROD,
        enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
        enableServiceWorker: !isDev && !isTauri(),
        enableNotifications: import.meta.env.VITE_ENABLE_NOTIFICATIONS !== 'false',
        groqApiEnabled: !!import.meta.env.VITE_GROQ_API_KEY,
        upstoxEnabled: !!import.meta.env.VITE_UPSTOX_API_KEY,
        debugMode: import.meta.env.VITE_DEBUG === 'true' || isDev,
        version: import.meta.env.VITE_APP_VERSION || '0.0.0',
        featureFlags,
    };
}

/** Get runtime configuration (singleton) */
export function getRuntimeConfig(): RuntimeConfig {
    if (!config) config = buildConfig();
    return config;
}

/** Check if a feature flag is enabled */
export function isFeatureEnabled(feature: FeatureFlagKey): boolean {
    return getRuntimeConfig().featureFlags[feature];
}

/** True when all demo/mock flows must be blocked and only live/backed APIs are allowed. */
export function isStrictLiveDataMode(): boolean {
    return isFeatureEnabled('strict_live_data_only');
}

/** Get API base URL for the current environment */
export function getApiBaseUrl(): string {
    return getRuntimeConfig().apiBaseUrl;
}

/** Get the current app variant */
export function getAppVariant(): RuntimeConfig['variant'] {
    return getRuntimeConfig().variant;
}

/** Reset config (for testing) */
export function resetConfig(): void {
    config = null;
}
